import React, { useState, useCallback, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  Chip,
  Alert,
  Button,
  Container,
  Grid,
  LinearProgress,
  Tooltip,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  Psychology,
  FileDownload,
  Refresh,
} from "@mui/icons-material";
import MUIDataTable from "mui-datatables";
import { withSnackbar } from "../../../utils/SnackbarHOC";
import {
  getScoredCandidates,
  runAgentScoring,
  exportCsv,
  exportPdf,
} from "../../../services/ScoringService";

const LEVEL_COLOR = {
  Excellent: { chip: "success", bar: "#2e7d32" },
  Bon:       { chip: "primary", bar: "#1976d2" },
  Moyen:     { chip: "warning", bar: "#ed6c02" },
  Faible:    { chip: "error",   bar: "#d32f2f" },
};

const ScoreBar = ({ value, level }) => {
  const color = LEVEL_COLOR[level]?.bar || "#9c27b0";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 90 }}>
        <LinearProgress
          variant="determinate"
          value={value}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "#f0f0f0",
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
          }}
        />
      </Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ color, minWidth: 38, textAlign: "right" }}
      >
        {value}%
      </Typography>
    </Box>
  );
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href    = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const ScoredCandidates = ({ snackbarShowMessage }) => {
  const history  = useHistory();
  const location = useLocation();
  const { missionId } = useParams();

  const missionName = location.state?.missionName || "Mission";

  const [scoringData,   setScoringData]   = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [agentRunning,  setAgentRunning]  = useState(false);
  const [exportLoading, setExportLoading] = useState({ csv: false, pdf: false });
  const [error,         setError]         = useState(null);

  const isSmall      = useMediaQuery((theme) => theme.breakpoints.between(0, 767));
  const isMediumMode = useMediaQuery((theme) => theme.breakpoints.between(767, 1023));
  const isLargeMode  = useMediaQuery((theme) => theme.breakpoints.between(1023, 2800));
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const tableBodyMaxHeight = `${
    isSmall      ? viewportHeight - 292
    : isMediumMode ? viewportHeight - 480
    : isLargeMode  ? viewportHeight - 500
    : viewportHeight - 500
  }px`;

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchScoring = useCallback(
    async (forceRefresh = false) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await getScoredCandidates(missionId, forceRefresh);
        setScoringData(data);
      } catch {
        setError(
          "Impossible de charger le scoring. Vérifiez que le microservice IA est disponible."
        );
      } finally {
        setLoading(false);
      }
    },
    [missionId]
  );

  useEffect(() => {
    fetchScoring();
  }, [fetchScoring]);

  const handleRunAgent = async () => {
    setAgentRunning(true);
    try {
      await runAgentScoring(missionId);
      await fetchScoring(true);
      snackbarShowMessage(
        `Scoring recalculé — ${scoringData?.total ?? ""} candidats`,
        "success",
        3000
      );
    } catch {
      snackbarShowMessage("Erreur lors du recalcul IA", "error", 4000);
    } finally {
      setAgentRunning(false);
    }
  };

  const handleExportCsv = async () => {
    setExportLoading((prev) => ({ ...prev, csv: true }));
    try {
      const { data } = await exportCsv(missionId);
      const date     = new Date().toISOString().slice(0, 10);
      downloadBlob(data, `scoring-${missionId}-${date}.csv`);
    } catch {
      snackbarShowMessage("Erreur export CSV", "error", 3000);
    } finally {
      setExportLoading((prev) => ({ ...prev, csv: false }));
    }
  };

  const handleExportPdf = async () => {
    setExportLoading((prev) => ({ ...prev, pdf: true }));
    try {
      const { data } = await exportPdf(missionId);
      const date     = new Date().toISOString().slice(0, 10);
      downloadBlob(data, `scoring-${missionId}-${date}.pdf`);
    } catch {
      snackbarShowMessage("Erreur export PDF", "error", 3000);
    } finally {
      setExportLoading((prev) => ({ ...prev, pdf: false }));
    }
  };

  const hasResults = (scoringData?.candidates?.length ?? 0) > 0;

  
  const columns = [
    {
      name: "rank",
      label: "#",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({ style: { width: 50 } }),
        customBodyRender: (value) => (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "full_name",
      label: "NOM",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2" fontWeight={500}>
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "score_percent",
      label: "SCORE",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({ style: { minWidth: 200 } }),
        customBodyRender: (value, tableMeta) => (
          <ScoreBar value={value} level={tableMeta.rowData[3]} />
        ),
      },
    },
    {
      name: "level",
      label: "NIVEAU",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Chip
            label={value}
            size="small"
            color={LEVEL_COLOR[value]?.chip || "default"}
            sx={{ fontWeight: 600 }}
          />
        ),
      },
    },
    {
      name: "experience_years",
      label: "EXPÉRIENCE",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2" color="text.secondary">
            {value != null ? `${value} ans` : "—"}
          </Typography>
        ),
      },
    },
  ];

  const tableOptions = {
    rowsPerPageOptions: [10, 25, 50, 100],
    jumpToPage: true,
    tableBodyMaxHeight,
    serverSide: false,
    selectableRows: "none",
    filter: true,
    filterType: "dropdown",
    search: true,
    download: false,
    print: false,
    viewColumns: true,
    responsive: "standard",
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#7c3aed" }} />
        ) : (
          "Aucun candidat scoré pour cette mission."
        ),
      },
      pagination: {
        next: "Page suivante",
        previous: "Page précédente",
        rowsPerPage: "Lignes par page :",
        displayRows: "sur",
        jumpToPage: "Page",
      },
      toolbar: {
        search: "Rechercher",
        viewColumns: "Colonnes",
        filterTable: "Filtrer",
      },
    },
    onRowClick: (_, rowMeta) => {
      const candidate = scoringData?.candidates?.[rowMeta.dataIndex];
      if (candidate?.candidate_id) {
        history.push(`/candidats/${candidate.candidate_id}/view`);
      }
    },
    setRowProps: (_, dataIndex) => ({
      style: { cursor: "pointer" },
    }),
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Container>
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <Box
              component="span"
              onClick={() => history.goBack()}
              sx={{
                cursor: "pointer",
                color: "#117aa7",
                display: "inline-flex",
                alignItems: "center",
                mb: 1,
              }}
            >
              <ArrowBack fontSize="small" />
            </Box>

            <Card sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700} color="#117aa7">
                    Candidats scorés — {scoringData?.job_title || missionName}
                  </Typography>
                  {scoringData && (
                    <Typography variant="body2" color="text.secondary">
                      {scoringData.total} candidat{scoringData.total > 1 ? "s" : ""} · Calculé le{" "}
                      {new Date(scoringData.computed_at).toLocaleString("fr-FR")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={
                  agentRunning ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Psychology />
                  )
                }
                onClick={handleRunAgent}
                disabled={agentRunning || loading}
                sx={{ bgcolor: "#117aa7", "&:hover": { bgcolor: "#0e6a94" } }}
              >
                {agentRunning ? "Recalcul en cours..." : "Lancer l'agent IA"}
              </Button>

              <Tooltip title={!hasResults ? "Aucun résultat à exporter" : ""}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={
                      exportLoading.csv ? (
                        <CircularProgress size={14} />
                      ) : (
                        <FileDownload />
                      )
                    }
                    onClick={handleExportCsv}
                    disabled={!hasResults || exportLoading.csv}
                  >
                    Télécharger CSV
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title={!hasResults ? "Aucun résultat à exporter" : ""}>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={
                      exportLoading.pdf ? (
                        <CircularProgress size={14} />
                      ) : (
                        <FileDownload />
                      )
                    }
                    onClick={handleExportPdf}
                    disabled={!hasResults || exportLoading.pdf}
                  >
                    Télécharger PDF
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12}>
            {error ? (
              <Box sx={{ p: 3 }}>
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<Refresh />}
                      onClick={() => fetchScoring()}
                    >
                      Réessayer
                    </Button>
                  }
                >
                  {error}
                </Alert>
              </Box>
            ) : (
              <MUIDataTable
                title="Candidats"
                data={scoringData?.candidates || []}
                columns={columns}
                options={tableOptions}
              />
            )}
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default withSnackbar(ScoredCandidates);
